export interface CalendarUISettings {
  minHour?: number;
  maxHour?: number;
  calendarStep?: number;
  /** Weekdays the week view shows, 0 = Sunday … 6 = Saturday (moment `d`). */
  visibleWeekdays?: number[];
  /** Minimum width of a resource column in px (the columns still stretch to fill). */
  columnWidth?: number;
}

export const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

export const DEFAULT_CALENDAR_COLUMN_WIDTH = 130;
export const MIN_CALENDAR_COLUMN_WIDTH = 80;
export const MAX_CALENDAR_COLUMN_WIDTH = 400;

export const DEFAULT_CALENDAR_UI_SETTINGS: CalendarUISettings = {
  minHour: 0,
  maxHour: 23,
  calendarStep: 60,
  visibleWeekdays: ALL_WEEKDAYS,
  columnWidth: DEFAULT_CALENDAR_COLUMN_WIDTH,
};

const MINUTES_PER_DAY = 24 * 60;

// Stored preferences round-trip through the API, so a value can come back as a
// string or a null, and older settings forms allowed hour combinations the
// current one no longer offers.
const toInteger = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : undefined;
};

// Unique, sorted weekday indexes; anything else is dropped. An empty result
// means "nothing valid was stored" — a week view with no days can't render, so
// the caller falls back to every day.
const toWeekdays = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const weekdays = [...new Set(value.map(toInteger))]
    .filter((day): day is number => day !== undefined && day >= 0 && day <= 6)
    .sort((a, b) => a - b);
  return weekdays.length > 0 ? weekdays : undefined;
};

/**
 * Keeps the calendar's time range renderable. An empty range (minHour === maxHour)
 * leaves the time grid with fewer slot groups than a group has slots, and the time
 * gutter then reads past the end of the group list (BACKOFFICE-366); a step of 0
 * asks for an infinite number of groups. Anything invalid falls back to the defaults.
 */
export const sanitizeCalendarUISettings = (
  settings?: CalendarUISettings | null,
): CalendarUISettings => {
  const minHour = toInteger(settings?.minHour) ?? DEFAULT_CALENDAR_UI_SETTINGS.minHour!;
  const maxHour = toInteger(settings?.maxHour) ?? DEFAULT_CALENDAR_UI_SETTINGS.maxHour!;
  const calendarStep = toInteger(settings?.calendarStep) ?? DEFAULT_CALENDAR_UI_SETTINGS.calendarStep!;
  const columnWidth = toInteger(settings?.columnWidth);

  const hoursAreValid = minHour >= 0 && maxHour <= 23 && minHour < maxHour;
  const stepIsValid = calendarStep > 0 && calendarStep <= MINUTES_PER_DAY;
  const columnWidthIsValid =
    columnWidth !== undefined &&
    columnWidth >= MIN_CALENDAR_COLUMN_WIDTH &&
    columnWidth <= MAX_CALENDAR_COLUMN_WIDTH;

  return {
    minHour: hoursAreValid ? minHour : DEFAULT_CALENDAR_UI_SETTINGS.minHour,
    maxHour: hoursAreValid ? maxHour : DEFAULT_CALENDAR_UI_SETTINGS.maxHour,
    calendarStep: stepIsValid ? calendarStep : DEFAULT_CALENDAR_UI_SETTINGS.calendarStep,
    visibleWeekdays: toWeekdays(settings?.visibleWeekdays) ?? DEFAULT_CALENDAR_UI_SETTINGS.visibleWeekdays,
    columnWidth: columnWidthIsValid ? columnWidth : DEFAULT_CALENDAR_UI_SETTINGS.columnWidth,
  };
};
